import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {dialOptions} from "../../utils/functions";
import {PhoneNumberDTO} from "../../models/phoneNumber";
import {createEmergencyContact, updateEmergencyContact} from "../../api/emergencyContactApi";
import {PhoneNumberRow} from "../components/PhoneNumberRow";
import DialogCloseButton from "../buttons/DialogCloseButton";
import {EmergencyContact} from "../../models/emergencyContact";
import MessageBox, {MessageType} from "../components/MessageBox";
import {useSaveHandler} from "../../hooks/SaveHandlerContext";

interface FormData {
    lastName: string;
    firstName: string;
    details: string;
    shareData: boolean;
    phoneNumbers: PhoneNumberDTO[];
}

type Props = {
    accountId: number;
    emergencyContact?: EmergencyContact | null;
    onClose: () => void;
    onSaved: () => void;
};

function EmergencyContactDialog({accountId, emergencyContact, onClose, onSaved}: Props) {
    const {t} = useTranslation();

    const [formData, setFormData] = useState<FormData>({
        lastName: "",
        firstName: "",
        details: "",
        shareData: false,
        phoneNumbers: [{countryCode: "", phoneNumber: ""}],
    });

    const [message, setMessage] = useState<MessageType>(null);
    const [disableSaving, setDisableSaving] = useState<boolean>(true);
    const { setHasUnsavedChanges } = useSaveHandler();

    // Daten beim Mount laden
    useEffect(() => {
        const fetchData = async () => {
            try {
                if (emergencyContact) {
                    setFormData({
                        lastName: emergencyContact.lastName ?? "",
                        firstName: emergencyContact.firstName ?? "",
                        details: emergencyContact.details ?? "",
                        shareData: emergencyContact.shareData ?? true,
                        phoneNumbers:
                            emergencyContact.phoneNumbers?.length > 0 ? emergencyContact.phoneNumbers : [{
                                countryCode: "",
                                phoneNumber: ""
                            }],
                    });
                }
            } catch {
                setMessage({type: "error", text: t("common.messages.loadError")});
            }
        };
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [emergencyContact]);

    const setDialCode = (index: number, dialCodeValue: string) => {
        const updatedPhones = [...formData.phoneNumbers];
        setDisableSaving(false);
        setHasUnsavedChanges(true);
        updatedPhones[index] = {
            ...updatedPhones[index],
            countryCode: dialCodeValue,
        };
        setFormData({...formData, phoneNumbers: updatedPhones});
    };

    const addPhoneNumber = () => {
        setHasUnsavedChanges(true);
        setFormData({
            ...formData,
            phoneNumbers: [...formData.phoneNumbers, {countryCode: "", phoneNumber: ""}],
        });
    };

    const removePhoneNumber = (index: number) => {
        if (formData.phoneNumbers[index].phoneNumber !== "") {
            setDisableSaving(false);
        }
        setHasUnsavedChanges(true);
        const updatedPhones = formData.phoneNumbers.filter((_, i) => i !== index);
        setFormData({
            ...formData,
            phoneNumbers: updatedPhones.length ? updatedPhones : [{countryCode: "", phoneNumber: ""}]
        });
    };

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
        index?: number
    ) => {
        const {name, value, type} = e.target;
        const isCheckbox = type === "checkbox";
        const checked = isCheckbox ? (e.target as HTMLInputElement).checked : undefined;

        setHasUnsavedChanges(true);
        setDisableSaving(false);

        if (index !== undefined) {
            const updatedPhones = [...formData.phoneNumbers];
            updatedPhones[index] = {
                ...updatedPhones[index],
                [name]: value,
            };
            setFormData({...formData, phoneNumbers: updatedPhones});
        } else {
            setFormData({
                ...formData,
                [name]: isCheckbox ? (checked as boolean) : value,
            });
        }
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setMessage(null);

        try {
            const dto = {
                accountId: accountId,
                lastName: formData.lastName,
                firstName: formData.firstName,
                details: formData.details,
                shareData: formData.shareData,
                phoneNumbers: formData.phoneNumbers
                    .filter((p) => p.countryCode && p.phoneNumber)
                    .map((p) => ({
                        countryCode: p.countryCode,
                        phoneNumber: p.phoneNumber
                    })),
            };

            if (!emergencyContact) {
                await createEmergencyContact(dto);
                onClose();
            } else {
                await updateEmergencyContact(emergencyContact.id, dto);
            }
            setMessage({type: "success", text: t("common.messages.success")});
            setDisableSaving(true);
            setHasUnsavedChanges(false);
            onSaved();
        } catch {
            setMessage({type: "error", text: t("common.messages.error")});
        }
    };

    const renderPhoneRow = (phone: PhoneNumberDTO, index: number) => {
        const multiple = formData.phoneNumbers.length > 1;
        return (
            <PhoneNumberRow
                phone={phone}
                index={index}
                dialOptions={dialOptions}
                onChange={handleChange}
                setDialCode={setDialCode}
                removePhoneNumber={removePhoneNumber}
                multiple={multiple}
            />
        );
    };

    return (
        <div className="dialog-overlay">
            <div className="dialog">
                <DialogCloseButton onClose={onClose}/>
                <>
                    <h3>{emergencyContact
                        ? t("emergency.dialog.edit")
                        : t("emergency.dialog.create")}</h3>
                </>

                <div className="scrollbar-area">
                    <form onSubmit={handleSubmit} className="form-container">
                        <MessageBox message={message}/>

                        {/* Vorname */}
                        <div className="form-group">
                            <label>{t("contact.fields.firstName")}</label>
                            <input
                                type="text"
                                name="firstName"
                                value={formData.firstName}
                                onChange={handleChange}
                                className="form-input"
                                required
                            />
                        </div>

                        {/* Nachname */}
                        <div className="form-group">
                            <label>{t("contact.fields.lastName")}</label>
                            <input
                                type="text"
                                name="lastName"
                                value={formData.lastName}
                                onChange={handleChange}
                                className="form-input"
                                required
                            />
                        </div>

                        {/* Details */}
                        <div className="form-group">
                            <label>{t("contact.fields.details")}</label>
                            <input
                                type="text"
                                name="details"
                                value={formData.details}
                                onChange={handleChange}
                                className="form-input"
                                required
                            />
                        </div>

                        {/* Telefonnummern */}
                        <div className="form-group">
                            <label>{t("contact.fields.phone")}</label>
                            {formData.phoneNumbers.map((phone, index) => renderPhoneRow(phone, index))}
                            <button
                                type="button"
                                onClick={addPhoneNumber}
                                className="form-button-secondary"
                            >
                                {/*<span className="emoji">&#x2795;</span>*/}
                                {t("common.button.addPhone")}
                            </button>
                        </div>

                        {/* Share */}
                        <label>
                            <input
                                type="checkbox"
                                name="shareData"
                                checked={formData.shareData}
                                onChange={handleChange}
                            />
                            {t("contact.fields.share")}
                        </label>

                        {/* Submit */}
                        <button
                            type="submit"
                            disabled={disableSaving}
                            className="form-button">
                            {t("common.button.save")}
                        </button>
                    </form>
                </div>


            </div>
        </div>
    );
}

export default EmergencyContactDialog;